import {useIsFocused, useNavigation} from '@react-navigation/native';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Text,
  View,
  DeviceEventEmitter,
  Keyboard,
} from 'react-native';
import Animated from 'react-native-reanimated';
import {useSafeAreaFrame, useSafeAreaInsets} from 'react-native-safe-area-context';
import {getListFromPages} from '~components/HomeScreenComponent/helpers/getListFromPages';
import {useAllEvents} from '~hooks/event/useAllEvents';
import {useHomeStore} from '~providers/home/zustand';
import {Event} from '~types/api/event';
import {EVENT_AGE_GROUP, ORDER_BY, ORDER_DIR, TABS} from '~types/events';
import ModernEventsList from '../components/ModernEventsList';
import {SCREENS, spacing} from '~constants';
import {getMapStyle} from '~constants/locationMap';
import {useTheme} from '~contexts/ThemeContext';
import MapView, {Marker, PROVIDER_GOOGLE, Region} from 'react-native-maps';
import BottomSheet from '@gorhom/bottom-sheet';

import useGetCurrentPosition from '~components/LocationModal/hooks/useGetCurrentPosition';
import LikedEventPin from '~components/map/LikedEventPin';
import EventDetailsBottomSheet from '../HomeContent/EventDetailsBottomSheet';
import {useMapEventPrefetch} from '~hooks/performance/useMapEventPrefetch';
import {useMapsContext} from '~providers/maps/zustand';
import { NavigationProps } from '~types/navigation/navigation.type';

interface LikedContenedInterface {
  scrollHandler: (event: NativeSyntheticEvent<NativeScrollEvent>) => void;
  filterEvents: (data: any, selectedTimeframe: {id: number; title: string}) => any;
  selectedTimeframe: {id: number; title: string};
  globalInputValue: string;
  isMapView?: boolean;
  setIsMapView?: (value: boolean) => void;
}

const LikedContened: React.FC<LikedContenedInterface> = ({
  scrollHandler,
  filterEvents,
  selectedTimeframe,
  globalInputValue,
  isMapView,
  setIsMapView,
}) => {
  const {colors} = useTheme();
  const {radius, isForKids} = useHomeStore();
  const {t} = useTranslation();
  const {data, isLoading, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, remove} = useAllEvents({
    distance_km: 10000000000000000,
    tab: TABS.DISCOVER,
    order_by: ORDER_BY.START_DATE,
    order_dir: ORDER_DIR.DESC,
    event_age_group: isForKids ? EVENT_AGE_GROUP.CHILDREN : null,
    filter_type: selectedTimeframe.title === t('home.upcoming_events') ? undefined : selectedTimeframe.title,
  });
  const {top} = useSafeAreaInsets();
  const {width, height} = useSafeAreaFrame();
  const isFocused = useIsFocused();

  // Map-related state and refs
  const sheetRef = useRef<BottomSheet>(null);
  const snapPoints = useMemo(() => ['1%', '100%'], []);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const mapRef = useRef<MapView>(null);
  const [currentLocation, setCurrentLocation] = useState({latitude: 51.5072, longitude: 0.1276});
  const {getCurrentPosition} = useGetCurrentPosition();
  const {currentPositionState} = useMapsContext();

  const eventsList = getListFromPages<Event>(data);
  const [refreshing, setRefreshing] = useState(false);
    const navigation = useNavigation<NavigationProps>();

  useEffect(() => {
    // Emit loading start event for search bar
    DeviceEventEmitter.emit('mapLoadingStart');
    remove();
    refetch();
  }, [refetch, remove, radius, isForKids, selectedTimeframe]);

  // Emit loading end event when loading states change
  useEffect(() => {
    if (!isLoading && !isFetching) {
      DeviceEventEmitter.emit('mapLoadingEnd');
    }
  }, [isLoading, isFetching]);

  // Map-related effects
  useEffect(() => {
    getCurrentPosition();
  }, []);

  useEffect(() => {
    DeviceEventEmitter.addListener('eventSelection', data => {
      const item = data.event;
      if (item) {
        navigation.navigate(SCREENS.HOME_EVENT, {
          tag: `${item.event_id}=tag`,
          eventId: item.event_id,
          item: item,
        });
      }
    });

    return () => {
      DeviceEventEmitter.removeAllListeners('eventSelection');
    };
  }, []);

  // Control BottomSheet position when map/list view changes
  useEffect(() => {
    if (sheetRef.current) {
      const targetIndex = isMapView ? 0 : 1;
      if (currentPosition !== targetIndex) {
        sheetRef.current.snapToIndex(targetIndex);
      }
    }
  }, [isMapView, currentPosition]);

  const handleLoadMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleSheetChange = useCallback((index: number) => {
    setSelectedEvent(null);
    setCurrentPosition(index);
  }, []);

  const contentLayout = useMemo(() => {
    const platformLayout = Platform.OS === 'ios' ? 115 : 125;
    return platformLayout + top;
  }, [top]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => {
      refetch();
      setRefreshing(false);
    }, 2000);
  }, []);

  const filteredEvents = useMemo(() => {
    if (!eventsList) {
      return [];
    }
    let events = eventsList;

    // Apply time-based filtering first
    if (events && filterEvents) {
      events = filterEvents(events, selectedTimeframe);
    }

    // Then apply search filtering if needed
    if (globalInputValue && events) {
      events = events.filter(event => event.name.toLowerCase().includes(globalInputValue.toLowerCase()));
    }

    return events || [];
  }, [eventsList, selectedTimeframe, globalInputValue, filterEvents]);

  const likedEvents = useMemo(() => {
    return filteredEvents.filter(item => item.user_liked === true);
  }, [filteredEvents]);

  // Map event prefetching - prefetch when 10 or fewer events
  const {prefetchEvent, isPrefetched} = useMapEventPrefetch({
    events: likedEvents || [],
    enabled: isMapView && (likedEvents?.length || 0) <= 10,
    maxEventsToPrefetch: 10,
    prefetchDelay: 1000,
    prefetchStrategy: 'delayed',
  });

  useEffect(() => {
    if (!isLoading && !isFetching && !isFetchingNextPage && filteredEvents?.length * 170 < height) {
      handleLoadMore();
    }
  }, [isLoading, isFetching, isFetchingNextPage, filteredEvents, handleLoadMore, height]);

  if (!(isLoading || isFetching || isFetchingNextPage) && !eventsList?.length) {
    return (
      <Text
        style={{
          marginTop: 250,
          textAlign: 'center',
          paddingHorizontal: 30,
          fontSize: 16,
          fontWeight: '500',
        }}>
        {t('home.empty_list_events_discover')}
      </Text>
    );
  }

  if (isLoading || !eventsList?.length) {
    return <></>;
  }
  if (!isFocused && isFetching) {
    return <></>;
  }

  return (
    <View style={{flex: 1}}>
      <View style={{flex: 1, marginTop: Platform.OS === 'ios' ? 75 + top : 85 + top}}>
        <View
          style={{
            flex: 1,
            transform: [{scale: 2.0}], // Increased to 2x scaling for maximum size
            // Adjust margins to compensate for 2x scaling
            marginTop: -100,
            marginBottom: -100,
            marginLeft: -100,
            marginRight: -100,
          }}>
          <MapView
            ref={mapRef}
            style={{flex: 1}}
            provider={PROVIDER_GOOGLE}
            customMapStyle={getMapStyle()}
            showsUserLocation={true}
            showsMyLocationButton={true}
            showsCompass={false}
            toolbarEnabled={false}
            showsBuildings={false}
            showsIndoors={false}
            initialRegion={{
              latitude: currentPositionState?.latitude ?? currentLocation.latitude,
              longitude: currentPositionState?.longitude ?? currentLocation.longitude,
              latitudeDelta: 0.04,
              longitudeDelta: 0.04,
            }}
            onPress={() => {
              Keyboard.dismiss();
              DeviceEventEmitter.emit('hideSuggestion');
            }}>
            {likedEvents?.map((event, index) => (
              <Marker
                key={`${event.event_id}-${index}`}
                coordinate={{
                  latitude: event.coords.lat,
                  longitude: event.coords.long,
                }}
                onPress={() => {
                  Keyboard.dismiss();
                  setSelectedEvent(event);

                  // Trigger immediate prefetch for this event if not already prefetched
                  if (!isPrefetched(event.event_id)) {
                    prefetchEvent(event.event_id);
                  }
                }}>
                <LikedEventPin
                  isSelected={selectedEvent?.event_id === event.event_id}
                  eventType={event.event_type as 'business' | 'influencer' | 'community' | 'pyxi_select'}
                  size={40}
                  enhancedVisibility={true}
                />
              </Marker>
            ))}
          </MapView>
        </View>
        <BottomSheet
          ref={sheetRef}
          snapPoints={snapPoints}
          index={isMapView ? 0 : 1}
          enablePanDownToClose={false}
          enableDynamicSizing={false}
          handleComponent={() => null}
          onChange={handleSheetChange}
          handleStyle={{backgroundColor: colors.background, borderRadius: 0}}
          backgroundStyle={{backgroundColor: colors.background, borderRadius: 0}}
          activeOffsetY={[-5, 5]}
          failOffsetX={[-999, 999]}
          enableContentPanningGesture={false}
          enableHandlePanningGesture={false}>
          <ModernEventsList
            data={likedEvents}
            isLoading={isLoading}
            isFetching={isFetching}
            isFetchingNextPage={isFetchingNextPage}
            refreshing={refreshing}
            onRefresh={onRefresh}
            onEndReached={handleLoadMore}
            onEndReachedThreshold={0.5}
            isMyEvents={false}
            emptyStateTitle="No liked events"
            emptyStateSubtitle="Start liking events to see them here!"
            estimatedItemSize={280}
          />
        </BottomSheet>
      </View>
      {selectedEvent && <EventDetailsBottomSheet event={selectedEvent} />}
    </View>
  );
};

export default LikedContened;
